import { Component, OnInit, OnDestroy, Injectable } from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';
import { getTitle, getDescription } from './lib/helpers';
import { SearchResult, SearchService } from './search.service';
import { CatalogService } from './catalog/catalog.service';
import { ActivatedRoute, ActivatedRouteSnapshot, Resolve, Router } from '@angular/router';
import { BreadCrumbsService } from './breadcrumbs.service';

const defaultNavParams = {
  iNumPage: 1,
  nPageSize: 6,
  checkOutOfRange: true
};

@Component({
  selector: 'search-page',
  templateUrl: '../templates/search-page.component.html',
  styleUrls: ['../styles/components/search-page.component.less']
})
export class SearchPageComponent implements OnInit, OnDestroy {
  searchPhrase = '';
  currentSearchPhrase = '';
  results: SearchResult[] = [];
  navParams: any = defaultNavParams;
  countPages: number = 0;
  isPaginationReady: boolean = false;
  loadingState: boolean = false;

  constructor(
    private searchService: SearchService,
    private catalogService: CatalogService,
    private route: ActivatedRoute,
    private titleService: Title,
    private metaService: Meta,
    private breadCrumbsService: BreadCrumbsService,
  ) {
    this.titleService.setTitle(getTitle('Поиск'));
    this.metaService.updateTag({name: 'description', content: getDescription('Поиск')});
    this.breadCrumbsService.create([{
      name: 'Поиск',
      color: 'general-primary'
    }]);
  }

  public ngOnInit() {
    this.route.data.subscribe((data: {data: {
      countPages: number,
      phrase: string,
      items: SearchResult[]
    }}) => {
      this.results = data.data.items;
      this.searchPhrase = data.data.phrase;
      this.countPages = data.data.countPages;
      this.isPaginationReady = true;
    });
  }

  public ngOnDestroy() {
    defaultNavParams.iNumPage = 1;
  }

  public async newSearch() {
    this.searchPhrase = this.currentSearchPhrase;
    this.isPaginationReady = false;

    try {
      const countItems = await this.searchService.getCount({q: this.searchPhrase});
      this.countPages = Math.ceil(countItems.count / this.navParams.nPageSize);

      this.navParams.iNumPage = 1;
      await this.loadPage();
    } catch (e) {}
    this.isPaginationReady = true;
  }

  public changePage(page: number) {
    this.navParams.iNumPage = page;
    this.loadPage();
  }

  public async loadPage() {
    this.loadingState = true;
    try {
      let data: SearchResult[] = await this.searchService.search({
        q: this.searchPhrase,
        navParams: this.navParams
      });

      data = data.filter((item) => item.PARAM1 === 'catalog');

      data.forEach(async(item, i) => {
        if (item.ITEM_ID.startsWith('S')) return;
        const el = await this.catalogService.getByCode(item.ITEM_ID);
        data[i].CRUMBS = [{
          name: 'Магазин',
          href: '/',
          color: 'general-primary'
        },
        {
          name: el.IBLOCK_SECTION.NAME,
          href: '/catalog/section/' + el.IBLOCK_SECTION.ID,
          color: 'general-primary'
        }];
      });
      this.results = data;
      this.loadingState = false;
      return this.results;
    } catch (e) {
      this.loadingState = false;
    }
  }
}

@Injectable()
export class SearchPageResolver implements Resolve<{
  countPages: number,
  phrase: string,
  items: SearchResult[]
}> {
  navParams: any = defaultNavParams;

  constructor(
    private searchService: SearchService,
    private catalogService: CatalogService,
    private router: Router
  ) {}

  public async resolve(route: ActivatedRouteSnapshot): Promise<{
    countPages: number,
    phrase: string,
    items: SearchResult[]
  }> {
    const query = route.queryParamMap.get('q');
    const res: {
      countPages: number,
      phrase: string,
      items: SearchResult[]
    } = {countPages: null, phrase: query, items: null};

    try {
      const countItems = await this.searchService.getCount({q: encodeURIComponent(query)});
      res.countPages = Math.ceil(countItems.count / this.navParams.nPageSize);

      res.items = await this.searchService.search({
        q: encodeURIComponent(query),
        navParams: this.navParams
      });

      res.items = res.items.filter((item) => item.PARAM1 === 'catalog');

      res.items.forEach(async(item, i) => {
        if (item.ITEM_ID.startsWith('S')) return;
        const el = await this.catalogService.getByCode(item.ITEM_ID);
        res.items[i].CRUMBS = [{
          name: 'Магазин',
          href: '/',
          color: 'general-primary'
        },
        {
          name: el.IBLOCK_SECTION.NAME,
          href: '/catalog/section/' + el.IBLOCK_SECTION.ID,
          color: 'general-primary'
        }];
      });

      return res;
    } catch (e) {
      this.router.navigate(['/404']);
      return null;
    }
  }
}
